import React from 'react'
import { Link, Stack } from '@mui/material'
import LaunchIcon from '@mui/icons-material/Launch'

import CountChip, { type CountChipItem } from '../../../components/inspector/shared/CountChip'
import { handleLightspeedLinkClick } from '../../../utils/externalLinks'
import { renderProductStatus } from '../../exceptions/shared/productRenderers'
import type { ExceptionConfig } from '../../exceptions/types'
import FamilyVariantsTable from '../../../pages/inspector/Tools/tools/components/FamilyVariantsTable'
import { fmtInt, fmtMoney } from './formatters'

export interface VariantRow {
  ls_id: string
  name: string
  variant_name: string | null
  sku: string | null
  active: boolean
  units_sold: number
  revenue: number
  is_parent_of_record: boolean
  lightspeed_variant_link?: string
}

export interface VariantUtilisationRow {
  parent_ls_id: string
  family_name: string
  family_sku: string | null
  parent_of_record_active: boolean
  variant_count: number
  sold_variant_count: number
  dormant_variant_count: number
  active_mismatch_count: number
  units_sold_total: number
  revenue_total: number
  lightspeed_product_link?: string
  variants: VariantRow[]
}

const toChipItem = (v: VariantRow, withSoldMetric = false): CountChipItem => {
  const label = v.variant_name || v.name
  const primary = v.is_parent_of_record ? `${label} (primary)` : label
  return {
    id: v.ls_id,
    primary,
    secondary: v.sku,
    link: v.lightspeed_variant_link,
    ...(withSoldMetric ? { metrics: [fmtInt(v.units_sold)] } : {}),
  }
}

const renderFamilyName = (
  _value: any,
  row: Record<string, any>,
): React.ReactNode => {
  const r = row as VariantUtilisationRow
  if (!r.lightspeed_product_link) return r.family_name
  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Link
        href={r.lightspeed_product_link}
        onClick={(e) =>
          handleLightspeedLinkClick(
            e,
            r.lightspeed_product_link!,
            `Product ${r.family_sku || r.family_name}`,
          )
        }
        sx={{
          textDecoration: 'none',
          '&:hover': { textDecoration: 'underline' },
          fontWeight: 500,
          fontSize: '0.95rem',
        }}
      >
        {r.family_name}
      </Link>
      <LaunchIcon fontSize="small" sx={{ color: 'text.secondary', fontSize: 14 }} />
    </Stack>
  )
}

const renderVariantCount = (
  _value: any,
  row: Record<string, any>,
): React.ReactNode => {
  const r = row as VariantUtilisationRow
  return (
    <CountChip
      count={r.variant_count}
      items={r.variants.map((v) => toChipItem(v, true))}
      label="Variants"
      metricHeaders={['Sold']}
    />
  )
}

const renderSoldVariantCount = (
  _value: any,
  row: Record<string, any>,
): React.ReactNode => {
  const r = row as VariantUtilisationRow
  const pct =
    r.variant_count > 0
      ? Math.round((r.sold_variant_count / r.variant_count) * 100)
      : 0
  const sold = r.variants.filter((v) => v.units_sold > 0)
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <CountChip
        count={r.sold_variant_count}
        items={sold.map((v) => toChipItem(v, true))}
        label="Sold in period"
        metricHeaders={['Sold']}
      />
      <span style={{ color: '#666', fontSize: '0.8125rem' }}>({pct}%)</span>
    </Stack>
  )
}

const renderDormantVariantCount = (
  _value: any,
  row: Record<string, any>,
): React.ReactNode => {
  const r = row as VariantUtilisationRow
  const dormant = r.variants.filter((v) => v.units_sold === 0)
  return (
    <CountChip
      count={r.dormant_variant_count}
      items={dormant.map((v) => toChipItem(v))}
      label="Dormant variants"
    />
  )
}

export const variantUtilisationConfig: ExceptionConfig = {
  codes: [],
  complexity: 'comparison',
  rowKeyField: 'parent_ls_id',
  defaultSortColumn: 'family_name',
  defaultSortDirection: 'asc',
  columns: [
    {
      key: 'family_name',
      label: 'Variant Group',
      align: 'left',
      minWidth: 320,
      render: renderFamilyName,
    },
    {
      key: 'family_sku',
      label: 'SKU',
      align: 'left',
      render: (v) => (v as string) || '-',
    },
    {
      key: 'parent_of_record_active',
      label: 'Status',
      render: (v) => renderProductStatus(v),
      getSortValue: (row) => (row.parent_of_record_active ? 1 : 0),
    },
    {
      key: 'variant_count',
      label: 'Variants',
      align: 'center',
      render: renderVariantCount,
    },
    {
      key: 'sold_variant_count',
      label: 'Sold in period',
      align: 'center',
      render: renderSoldVariantCount,
      getSortValue: (row) => row.sold_variant_count,
    },
    {
      key: 'dormant_variant_count',
      label: 'Dormant',
      align: 'center',
      render: renderDormantVariantCount,
    },
    {
      key: 'units_sold_total',
      label: 'Units sold (total)',
      align: 'center',
      render: (v) => fmtInt(v as number),
    },
    {
      key: 'revenue_total',
      label: 'Revenue (total)',
      align: 'right',
      render: (v) => fmtMoney(v as number),
    },
  ],
  expandable: {
    renderContent: (row) => (
      <FamilyVariantsTable family={row as VariantUtilisationRow} />
    ),
    rowClick: true,
  },
}
